'use client';

import { cn } from '@/shared/lib/cn';
import type { VariantNudge } from '@/types/concept';

interface VariantNudgeCalloutProps {
  nudge: VariantNudge;
  /**
   * Switch the next render to Explore ("New variant"). When omitted the callout
   * is purely informational (a `div`, not a `button`) — used by the
   * `choose_path` nudge which only orients the user to both options.
   */
  onActivate?: () => void;
  /**
   * Horizontal caret position as a fraction of the callout width. `0.25` aims it
   * under the LEFT "New variant" segment (new_variant nudge); `0.5` centers it on
   * the whole toggle (choose_path nudge). Defaults to `0.25`.
   */
  caretFraction?: number;
  /**
   * Which edge carries the caret: `top` points UP at a toggle ABOVE the callout;
   * `bottom` points DOWN at a toggle BELOW it (used when the nudge renders above
   * the render-mode toggle). Defaults to `top`.
   */
  caretEdge?: 'top' | 'bottom';
  className?: string;
}

/**
 * Server-driven nudge callout shown next to the Explore/Refine toggle. It
 * mirrors the centralized `NoticeBanner` info variant (teal #D6E6E6 family) and
 * adds a caret on the edge facing the toggle — pointing down at a toggle below
 * it (`caretEdge="bottom"`, the concept-page arrangement) or up at one above it
 * (`"top"`) — see RenderModeToggle. Two server nudge types use it:
 *  - `new_variant`: tapping selects Explore mode via `onActivate`.
 *  - `choose_path` (right after the first render): caret CENTERED
 *    (`caretFraction` 0.5) on the whole toggle, informational only (no
 *    `onActivate`) — it speaks to both options (new variant vs. refine this one).
 *
 * Title + message come pre-localized from the API and are rendered AS-IS
 * (project rule: API-driven messages — no client-side copy).
 *
 * The callout stays visible until the server recomputes `variant_nudge` after
 * the user actually creates a new variant — selecting the mode alone does not
 * dismiss it.
 */
export function VariantNudgeCallout({
  nudge,
  onActivate,
  caretFraction = 0.25,
  caretEdge = 'top',
  className,
}: VariantNudgeCalloutProps) {
  // Same footprint cap/centering as the toggle below so the caret lines up under
  // the targeted segment.
  const wrapperClassName = cn(
    'relative w-full max-w-md mx-auto block text-left',
    className
  );

  const inner = (
    <>
      {/* Body — mirrors NoticeBanner info variant tokens exactly. Own uniform,
          smaller corner radius (rounded-lg, ~8px) so the message is clearly
          distinct in shape from the larger settings-group cards. The full inset
          ring outlines all four edges. */}
      <div
        className={cn(
          'relative p-4 rounded-lg',
          'bg-[#D6E6E6] dark:bg-teal-900/20',
          'shadow-[inset_0_0_0_1px_rgba(7,141,134,0.18)] dark:shadow-[inset_0_0_0_1px_rgba(15,118,110,0.45)]',
          // Hover feedback only when the callout is actually tappable
          // (new_variant). The informational choose_path variant has no action.
          onActivate &&
            'transition-colors hover:bg-[#cfe2e2] dark:hover:bg-teal-900/30'
        )}
      >
        <div className="text-form-label leading-[1.15] font-semibold text-foreground dark:text-teal-400">
          {nudge.title}
        </div>
        <div className="text-card-meta leading-[1.15] mt-1 text-[#2D3A3A] dark:text-teal-400">
          {nudge.message}
        </div>
      </div>
      {/* Guiding caret on the edge facing the toggle. A rotated square with
          only two adjacent borders showing reads as a triangle whose tip points
          at the toggle; the teal fill matches the callout body so it looks like
          a speech-bubble tail. The `left` (driven by caretFraction) +
          -translate-x-1/2 centers the tip at that fraction of the shared
          max-w-md footprint — 25% sits over the left "New variant" (Explore)
          half, 50% centers it on the whole toggle (choose_path).

          Rendered AFTER the body so it stacks on top: half of the rotated
          square overlaps into the body and its solid teal fill MASKS the body's
          border ring exactly under the caret, while the caret's own two-sided
          inset border carries the outline outward. The result is a seamless
          join — no seam line, no partial line — where the caret grows out of
          the banner body (mirrors Apple). The 4px overlap (-top-1 / -bottom-1)
          keeps enough coverage to hide the seam. Top edge shows the top+left
          borders (tip up); bottom edge shows bottom+right (tip down). */}
      <span
        aria-hidden
        style={{ left: `${caretFraction * 100}%` }}
        className={cn(
          'absolute -translate-x-1/2 h-3 w-3 rotate-45 rounded-[2px]',
          'bg-[#D6E6E6] dark:bg-teal-900/20',
          caretEdge === 'top'
            ? '-top-1 shadow-[inset_1px_1px_0_0_rgba(7,141,134,0.18)] dark:shadow-[inset_1px_1px_0_0_rgba(15,118,110,0.45)]'
            : '-bottom-1 shadow-[inset_-1px_-1px_0_0_rgba(7,141,134,0.18)] dark:shadow-[inset_-1px_-1px_0_0_rgba(15,118,110,0.45)]'
        )}
      />
    </>
  );

  // new_variant: tappable button → switches to Explore. choose_path: purely
  // informational, rendered as a non-interactive div (no a11y button role).
  if (onActivate) {
    return (
      <button type="button" onClick={onActivate} className={wrapperClassName}>
        {inner}
      </button>
    );
  }

  return <div className={wrapperClassName}>{inner}</div>;
}
